import { useEffect } from 'react';
import { TextInput, useRecordContext } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Stack, Typography } from '@mui/material';
import {
  CoronerAddress,
  formatCoronerAddress,
  formatMailingBlock,
  parseCoronerAddress,
} from './coronerAddress';

// Structured mailing address for a coroner. The cheque sender reads these
// columns; the legacy single-line `address` is rebuilt from them on save (see
// coronerAddressTransform), so the two never drift apart.
export const CoronerAddressInputs = () => (
  <Box sx={{ width: '100%', maxWidth: 640 }}>
    <PrefillFromLegacyAddress />
    <TextInput source="address_line1" label="Street address" fullWidth />
    <TextInput
      source="address_line2"
      label="Unit / Apt / Suite"
      helperText="e.g. Unit 4, Suite 200, App. 5 — printed on its own line on the cheque"
      fullWidth
    />
    <Stack direction="row" spacing={1.5}>
      <TextInput source="city" fullWidth />
      <TextInput source="province" sx={{ width: 120 }} />
      <TextInput source="postal_code" label="Postal code" sx={{ width: 160 }} />
    </Stack>
    <MailingPreview />
  </Box>
);

// Rebuild `address` from the structured fields before saving.
export function coronerAddressTransform<T extends Record<string, any>>(data: T): T {
  const addr = formAddress(data);
  const line = formatCoronerAddress(addr);
  return {
    ...data,
    address_line2: addr.addressLine2 || null,
    address: line || data.address || null,
  };
}

function formAddress(v: Record<string, any>): CoronerAddress {
  const t = (s: unknown) => (typeof s === 'string' ? s.trim() : '');
  return {
    addressLine1: t(v.address_line1),
    addressLine2: t(v.address_line2) || null,
    city: t(v.city),
    provinceOrState: t(v.province),
    postalOrZip: t(v.postal_code),
  };
}

// Older rows only have the single-line `address`. Split it (unit included)
// into the structured fields so the operator edits those instead.
const PrefillFromLegacyAddress = () => {
  const record = useRecordContext();
  const { setValue } = useFormContext();
  useEffect(() => {
    if (!record || record.address_line1 || !record.address) return;
    const parsed = parseCoronerAddress(record.address);
    if (!parsed) return;
    const opts = { shouldDirty: true };
    setValue('address_line1', parsed.addressLine1, opts);
    setValue('address_line2', parsed.addressLine2 ?? '', opts);
    setValue('city', parsed.city, opts);
    setValue('province', parsed.provinceOrState, opts);
    setValue('postal_code', parsed.postalOrZip, opts);
  }, [record, setValue]);
  return null;
};

const MailingPreview = () => {
  const values = useWatch();
  const name = typeof values.name === 'string' && values.name.trim() ? `Dr. ${values.name.trim()}` : 'Dr. …';
  const lines = formatMailingBlock(name, formAddress(values));
  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Cheque mailing address
      </Typography>
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1, fontFamily: 'monospace', fontSize: 13 }}>
        {lines.map((l, i) => (
          <div key={i}>{l}</div>
        ))}
      </Box>
    </Box>
  );
};
